/**
 * Ad destination links, made safe to hand to `window.open`.
 *
 * Links are typed by hand in the superadmin console, so they arrive in whatever
 * shape the author pasted — `www.dentcloudshop.com` as often as a full URL. A
 * protocol-less string is a *relative* path to `window.open`, which quietly
 * sends the user to `/clinic/branch/www.dentcloudshop.com` inside the app
 * instead of out to the advertiser, so the protocol is filled in here rather
 * than relying on the data being clean.
 */

/**
 * Schemes an ad is allowed to send a user to.
 *
 * Ad content is authored in a console, but the value still ends up in
 * `window.open`, so `javascript:` and `data:` links are dropped rather than
 * normalized — an ad has no legitimate reason to carry one.
 */
const ALLOWED_SCHEMES = ["http:", "https:", "mailto:", "tel:"];

/**
 * A link ready for `window.open`, or null when there is nothing safe to open.
 *
 * @param {string} url raw link from ad content
 * @returns {string|null}
 */
export function normalizeAdLink(url) {
  if (typeof url !== "string") return null;

  const trimmed = url.trim();
  if (!trimmed) return null;

  // Protocol-relative: inherit the page's scheme the way a browser would.
  if (trimmed.startsWith("//")) return `https:${trimmed}`;

  // An in-app path is already unambiguous; leave it alone.
  if (trimmed.startsWith("/")) return trimmed;

  const scheme = trimmed.match(/^[a-zA-Z][a-zA-Z0-9+.-]*:/);

  if (!scheme) return `https://${trimmed}`;

  return ALLOWED_SCHEMES.includes(scheme[0].toLowerCase()) ? trimmed : null;
}

/**
 * Open an ad's link in a new tab, normalizing it first.
 *
 * @param {string} url raw link from ad content
 * @param {string} [features] window features, defaults to the safe pairing
 * @returns {boolean} whether a link was actually opened
 */
export function openAdLinkInNewTab(url, features = "noopener,noreferrer") {
  const href = normalizeAdLink(url);
  if (!href) return false;

  window.open(href, "_blank", features);
  return true;
}

export default { normalizeAdLink, openAdLinkInNewTab };
